import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Card from '../../components/UI/Card';
import Modal from '../../components/UI/Modal';
import { testimonialsAPI } from '../../services/api';
import { getStorageUrl } from '../../utils/imageHelpers';
import { Plus, Edit, Trash2, Image as ImageIcon, Save, X, Eye, EyeOff } from 'lucide-react';

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const TestimonialCard = styled(Card)`
  padding: 1.5rem;
  position: relative;
`;

const TestimonialImage = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  margin: 0 auto 1rem;
  background-color: ${props => props.theme.colors.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const TestimonialName = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 0.5rem 0;
  text-align: center;
`;

const TestimonialPosition = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin: 0 0 1rem 0;
  text-align: center;
`;

const TestimonialText = styled.p`
  font-size: ${props => props.theme.typography.fontSize.base};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 1rem 0;
  font-style: italic;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const StatusBadge = styled.span`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.25rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  background-color: ${props => props.$active ? props.theme.colors.success + '20' : props.theme.colors.error + '20'};
  color: ${props => props.$active ? props.theme.colors.success : props.theme.colors.error};
`;

const ModalContent = styled.div`
  padding: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: ${props => props.rows ? props.rows * 1.5 : 4}rem;
  padding: 0.75rem;
  font-size: ${props => props.theme.typography.fontSize.base};
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
`;

const ImageUploadArea = styled.div`
  border: 2px dashed ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.primary}05;
  }
`;

const PreviewImage = styled.div`
  margin-top: 1rem;
  
  img {
    max-width: 100%;
    max-height: 200px;
    border-radius: ${props => props.theme.borderRadius.md};
  }
`;

const TestimonialsManagement = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    organization: '',
    testimonial: '',
    image: null,
    order: 0,
    is_active: true
  });
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const response = await testimonialsAPI.getAll({ all: true });
      setTestimonials(response.data || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (testimonial = null) => {
    if (testimonial) {
      setEditingTestimonial(testimonial);
      setFormData({
        name: testimonial.name || '',
        position: testimonial.position || '',
        organization: testimonial.organization || '',
        testimonial: testimonial.testimonial || '',
        image: null,
        order: testimonial.order || 0,
        is_active: testimonial.is_active !== undefined ? testimonial.is_active : true
      });
      setImagePreview(testimonial.image_url ? getStorageUrl(testimonial.image_url) : null);
    } else {
      setEditingTestimonial(null);
      setFormData({
        name: '',
        position: '',
        organization: '',
        testimonial: '',
        image: null,
        order: 0,
        is_active: true
      });
      setImagePreview(null);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTestimonial(null);
    setFormData({
      name: '',
      position: '',
      organization: '',
      testimonial: '',
      image: null,
      order: 0,
      is_active: true
    });
    setImagePreview(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Check if user is authenticated
      const token = localStorage.getItem('token');
      if (!token) {
        alert('You are not logged in. Please log in and try again.');
        window.location.href = '/login';
        return;
      }

      console.log('Submitting testimonial data:', formData);
      if (editingTestimonial) {
        await testimonialsAPI.update(editingTestimonial.id, formData);
      } else {
        await testimonialsAPI.create(formData);
      }
      handleCloseModal();
      fetchTestimonials();
    } catch (error) {
      console.error('Error saving testimonial:', error);
      console.error('Error response:', error.response?.data);
      if (error.response?.status === 401) {
        alert('Your session has expired. Please log in again.');
        window.location.href = '/login';
      } else {
        alert('Failed to save testimonial. Please try again.');
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      try {
        await testimonialsAPI.delete(id);
        fetchTestimonials();
      } catch (error) {
        console.error('Error deleting testimonial:', error);
        alert('Failed to delete testimonial. Please try again.');
      }
    }
  };

  return (
    <Container>
      <Header>
        <Title>Testimonials Management</Title>
        <Button variant="primary" onClick={() => handleOpenModal()}>
          <Plus size={20} style={{ marginRight: '0.5rem' }} />
          Add Testimonial
        </Button>
      </Header>

      {loading ? (
        <p>Loading testimonials...</p>
      ) : testimonials.length === 0 ? (
        <Card style={{ padding: '3rem', textAlign: 'center' }}>
          <p>No testimonials yet. Click "Add Testimonial" to create one.</p>
        </Card>
      ) : (
        <Grid>
          {testimonials.map((testimonial) => (
            <TestimonialCard key={testimonial.id}>
              <StatusBadge $active={testimonial.is_active}>
                {testimonial.is_active ? 'Active' : 'Inactive'}
              </StatusBadge>
              <TestimonialImage>
                {testimonial.image_url ? (
                  <img src={getStorageUrl(testimonial.image_url)} alt={testimonial.name} />
                ) : (
                  <ImageIcon size={32} style={{ opacity: 0.3 }} />
                )}
              </TestimonialImage>
              <TestimonialName>{testimonial.name}</TestimonialName>
              <TestimonialPosition>
                {testimonial.position}
                {testimonial.organization && ` at ${testimonial.organization}`}
              </TestimonialPosition>
              <TestimonialText>{testimonial.testimonial}</TestimonialText>
              <Actions>
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => handleOpenModal(testimonial)}
                >
                  <Edit size={16} />
                </Button>
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => handleDelete(testimonial.id)}
                >
                  <Trash2 size={16} />
                </Button>
              </Actions>
            </TestimonialCard>
          ))}
        </Grid>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingTestimonial ? 'Edit Testimonial' : 'Add Testimonial'}
      >
        <ModalContent>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Name *</Label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Full Name"
              />
            </FormGroup>

            <FormGroup>
              <Label>Position</Label>
              <Input
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                placeholder="e.g., Director, Program Manager"
              />
            </FormGroup>

            <FormGroup>
              <Label>Organization</Label>
              <Input
                name="organization"
                value={formData.organization}
                onChange={handleInputChange}
                placeholder="Organization name"
              />
            </FormGroup>

            <FormGroup>
              <Label>Testimonial *</Label>
              <Textarea
                name="testimonial"
                value={formData.testimonial}
                onChange={handleInputChange}
                rows={4}
                required
                placeholder="Enter the testimonial text..."
              />
            </FormGroup>

            <FormGroup>
              <Label>Image</Label>
              <ImageUploadArea onClick={() => document.getElementById('testimonialImageInput').click()}>
                <ImageIcon size={32} style={{ margin: '0 auto 0.5rem', display: 'block' }} />
                <p>Click to upload image</p>
                <p style={{ fontSize: '0.875rem', opacity: 0.7 }}>JPG, PNG or GIF (Max 5MB)</p>
              </ImageUploadArea>
              <input
                id="testimonialImageInput"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageChange}
              />
              {imagePreview && (
                <PreviewImage>
                  <img src={imagePreview} alt="Preview" />
                </PreviewImage>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Order</Label>
              <Input
                name="order"
                type="number"
                value={formData.order}
                onChange={handleInputChange}
                min="0"
                placeholder="Display order (lower numbers appear first)"
              />
            </FormGroup>

            <FormGroup>
              <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                Active (visible on landing page)
              </Label>
            </FormGroup>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '2rem' }}>
              <Button type="button" variant="outline" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                <Save size={20} style={{ marginRight: '0.5rem' }} />
                {editingTestimonial ? 'Update' : 'Create'} Testimonial
              </Button>
            </div>
          </form>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default TestimonialsManagement;


